<template>
  <div
    :class="{
      'select-text-input-fi': true,
      'select-text-input-fi--hover-disabled': !enableHover,
      'select-text-input-fi--disabled': disabled,
    }"
  >
    <div
      v-if="!fischer.app.isAppView()"
      v-click-outside="closeOptions"
      class="select-text-input-fi__container"
    >
      <div
        ref="selectInput"
        :class="{
          'select-text-input-fi__input-wrapper': true,
          'select-text-input-fi__input-wrapper--open': showOptions,
        }"
      >
        <text-input-fi
          class="select-text-input-fi__input"
          :model-value="inputValue"
          :label="label ?? undefined"
          :error="optionNotFound"
          :error-message="optionNotFound ? 'NOT FOUND' : undefined"
          :rules="rules"
          :required="required"
          :disabled="disabled"
          :light="light"
          @focus="showOptions = true"
          @input="inputChanged"
          @keyup-enter="setOption"
        />

        <icon-fi
          icon="chevron-down"
          class="select-text-input-fi__icon"
        />
      </div>

      <teleport
        v-if="isMounted"
        to="body"
      >
        <div
          v-if="showOptions"
          ref="selectOptionsContainer"
          :style="wrapperStyle"
          class="select-text-input-fi__options"
          @mouseenter="enableHover = true"
          @mouseleave="onMouseLeave"
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-for="option in filteredOptions"
            :key="option.key"
            ref="selectOption"
            :class="{
              'select-text-input-fi__option': true,
              'select-text-input-fi__option--focused': focused === option.key,
              'select-text-input-fi__option--selected': option.key === value,
            }"
            data-test="select-text-input-option"
            :data-test-2="option.name"
            @click="changeOption(option.name)"
            @mouseenter="onMouseEnter(option.key)"
            v-html="highlightText(option.name)"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';

import IconFi from '../icon-fi/IconFi.vue';
import TextInputFi from '../text-input-fi/TextInputFi.vue';

defineOptions({ name: 'SelectTextInputFi' });

const props = withDefaults(
  defineProps<{
    value?: string | null;
    options?: any[] | null;
    label?: string | null;
    rules?: any[];
    highlight?: boolean;
    required?: boolean;
    disabled?: boolean;
    light?: boolean;
  }>(),
  {
    value: null,
    options: null,
    label: null,
    rules: () => [],
    highlight: false,
    required: false,
    disabled: false,
    light: false,
  },
);

const emit = defineEmits<{
  (e: 'change', value: any): void;
}>();

const selectInput = useTemplateRef('selectInput');
const selectOptionsContainer = useTemplateRef('selectOptionsContainer');
const selectOption = useTemplateRef('selectOption');

const inputValue = ref<string | null>(null);
const inputValuePrev = ref<string | null>(null);
const focused = ref<any>(null);
const enableHover = ref(false);
const showOptions = ref(false);
const optionsCounter = ref(-1);
const wrapperStyle = reactive<Record<string, any>>({});
const optionNotFound = ref(false);
const focusedIndex = ref<number | null>(0);
const selectedIndex = ref<number | null>(null);
const optionsContainerEdge = ref<Record<string, any>>({});
const focusedOptionEdge = ref<Record<string, any>>({});
const focusedOptionBoundings = ref<any>(null);
const selectedOptionEdge = ref<Record<string, any>>({});
const popupPositioningEnabled = ref(false);
const isMounted = ref(false);

const { windowDimensions, windowScrollPos } = useBreakpoints();

const filteredOptions = computed<any[]>(() => {
  let options = props.options || [];
  const hasKeyProps = options?.[0] && Object.hasOwnProperty.call(options[0], 'key');

  // adds an additional key property if the given options have none.
  if (!hasKeyProps) {
    options = options.map((o) => ({ ...o, key: o.value }));
  }

  if (inputValue.value && inputValue.value !== '') {
    return options.filter((option) =>
      option.name.toLowerCase().includes((inputValue.value as string).toLowerCase()),
    );
  }

  return options;
});

function calculateBoundings() {
  if (IS_SSR) {
    return;
  }

  if (!selectOptionsContainer.value) {
    return;
  }

  const options = selectOption.value;
  const style = window?.getComputedStyle(selectOptionsContainer.value);
  focusedOptionBoundings.value = options?.[focusedIndex.value as number]?.getBoundingClientRect();

  if (!style || !options || !focusedOptionBoundings.value) {
    return;
  }

  optionsContainerEdge.value = {
    top: Number.parseInt(style.top, 10),
    bottom: Number.parseInt(style.top, 10) + Number.parseInt(style.height, 10),
  };

  focusedOptionEdge.value = {
    top: focusedOptionBoundings.value.top,
    bottom: focusedOptionBoundings.value.top + focusedOptionBoundings.value.height,
  };

  if (!props.value) {
    return;
  }

  const selectedOption = filteredOptions.value.find((i) => i.key === props.value);
  selectedIndex.value = filteredOptions.value.indexOf(selectedOption);

  if (selectedIndex.value === -1) {
    return;
  }

  const selectedOptionBoundings = options[selectedIndex.value]?.getBoundingClientRect();

  if (!selectedOptionBoundings) {
    return;
  }

  selectedOptionEdge.value = {
    top: selectedOptionBoundings.top,
    bottom: selectedOptionBoundings.top + selectedOptionBoundings.height,
  };
}

function positionPopup() {
  if (IS_SSR || !selectInput.value) {
    return;
  }

  const selectBounding = selectInput.value.getBoundingClientRect();
  const input = selectInput.value.querySelector('input')?.getBoundingClientRect();
  const top = selectBounding.top + (input?.height ?? 0);
  wrapperStyle.top = `${top}px`;
  wrapperStyle.left = `${selectBounding.left}px`;
  wrapperStyle.width = `${selectBounding.width}px`;
}

function updateScrollPosition(scrollDown: boolean) {
  calculateBoundings();

  if (scrollDown) {
    if (
      !selectOptionsContainer.value ||
      focusedOptionEdge.value.bottom <= optionsContainerEdge.value.bottom
    ) {
      return;
    }

    selectOptionsContainer.value.scrollTop += focusedOptionBoundings.value?.height;
  } else {
    if (
      !selectOptionsContainer.value ||
      focusedOptionEdge.value.top >= optionsContainerEdge.value.top
    ) {
      return;
    }

    selectOptionsContainer.value.scrollTop -= focusedOptionBoundings.value?.height;
  }
}

function handleKeys(event: KeyboardEvent) {
  focusedIndex.value = filteredOptions.value.indexOf(
    filteredOptions.value.find((i) => i.key === focused.value),
  );

  if (event.key === 'ArrowUp') {
    if (optionsCounter.value > -1) {
      if (focusedIndex.value) {
        optionsCounter.value = focusedIndex.value;
      }

      optionsCounter.value -= 1;
    }
  } else if (event.key === 'ArrowDown') {
    if (optionsCounter.value < filteredOptions.value.length - 1) {
      if (focusedIndex.value) {
        optionsCounter.value = focusedIndex.value;
      }

      optionsCounter.value += 1;
    }
  }

  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    enableHover.value = false;
    focused.value = filteredOptions.value[optionsCounter.value]?.key;
    focusedIndex.value = optionsCounter.value;
    updateScrollPosition(event.key === 'ArrowDown');
  }
}

function highlightText(text: string) {
  if (props.highlight) {
    return text.replace(
      new RegExp(inputValue.value as string, 'i'),
      (match) => `<span class="fi:semibold">${match}</span>`,
    );
  }
  return text;
}

function changeOption(value: any) {
  showOptions.value = false;
  const option = (props.options || []).find((el) => el.name === value);
  if (option?.value) {
    optionNotFound.value = false;
    emit('change', option.value);
  } else {
    focusedIndex.value = null;
    selectedIndex.value = null;
    inputValuePrev.value = null;
    selectedOptionEdge.value = {};
    optionNotFound.value = true;
    emit('change', '');
  }
}

function setOption() {
  const val = filteredOptions.value.find((i) => i.key === focused.value)?.name;
  if (val) {
    inputValue.value = val;
  }
  changeOption(val);
  selectInput.value?.querySelector('input')?.blur();
}

function onMouseEnter(optionKey: any) {
  enableHover.value = true;
  focused.value = optionKey;
}

function onMouseLeave() {
  enableHover.value = false;
  focused.value = filteredOptions.value?.[0]?.key;
}

function inputChanged(event: any) {
  enableHover.value = false;
  optionNotFound.value = false;
  showOptions.value = true;
  optionsCounter.value = 0;
  inputValue.value = event?.target?.value || '';
  focused.value = filteredOptions.value[0]?.key;
}

function closeOptions(event: any) {
  const optionsContainer = selectOptionsContainer.value;
  if (optionsContainer && event?.target && optionsContainer.contains(event.target)) {
    return;
  }

  if (showOptions.value) {
    showOptions.value = false;

    if (inputValue.value) {
      setOption();
    }
  }
}

watch(
  () => props.value,
  (val) => {
    inputValue.value = (props.options || []).find((option) => option.value === val)?.name || '';
    inputValuePrev.value = inputValue.value;
  },
  { immediate: true },
);

watch(
  windowScrollPos,
  () => {
    if (popupPositioningEnabled.value) {
      positionPopup();
    }
  },
  { deep: true },
);

watch(
  windowDimensions,
  () => {
    if (popupPositioningEnabled.value) {
      positionPopup();
    }
  },
  { deep: true },
);

watch(showOptions, async (value) => {
  if (IS_SSR) {
    return;
  }

  if (value) {
    inputValue.value = '';
    enableHover.value = false;
    optionsCounter.value = 0;
    focused.value = filteredOptions.value[optionsCounter.value]?.key;

    if (optionNotFound.value) {
      optionNotFound.value = false;
      emit('change', true);
      await nextTick();
    }

    positionPopup();

    setTimeout(() => {
      calculateBoundings();

      if (
        selectOptionsContainer.value &&
        selectedOptionEdge.value?.bottom > optionsContainerEdge.value.bottom
      ) {
        selectOptionsContainer.value.scrollTop =
          selectedOptionEdge.value?.top - optionsContainerEdge.value.top - 3;
      }

      focused.value = filteredOptions.value[selectedIndex.value as number]?.key;
    }, 0);
  } else {
    optionsCounter.value = -1;
    if (inputValuePrev.value) {
      inputValue.value = inputValuePrev.value;
      optionNotFound.value = false;
    }
  }

  popupPositioningEnabled.value = value;
});

onMounted(() => {
  isMounted.value = true;
  if (!IS_SSR) {
    window.addEventListener('keydown', handleKeys);
  }
});

onBeforeUnmount(() => {
  if (IS_SSR) {
    return;
  }

  window.removeEventListener('keydown', handleKeys);
});
const fischer = useFischer();
</script>

<style scoped src="./select-text-input-fi" />
